import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { IAuthenticationInfo } from '../../models/models-index';
import { environment } from '../../../../environments/environment';
import { AppInsights } from 'applicationinsights-js';
import { Store } from '@ngrx/store';
import { AppState } from '../../../_store/app-state.model';
import { AppSelectors } from '../../../_store/selector-types';
import { tap } from 'rxjs/operators';
import { RuntimeConfigService } from '../services-index';

export interface IPropDictionary {
  [key: string]: string;
}

export interface IMeasurementDictionary {
  [key: string]: number;
}

export interface IAppInsightService {
  trackPageView(options?: { pageName?: string, url?: string, props?: IPropDictionary, measurements?: IMeasurementDictionary }): void;
  trackEvent(eventName: string, options?: { props?: IPropDictionary, measurements?: IMeasurementDictionary }): void;
  trackException(ex: Error, options?: { handledAt?: string, props?: IPropDictionary, measurements?: IMeasurementDictionary }): void;
  trackTrace(message: string, options?: { props?: IPropDictionary, measurements?: IMeasurementDictionary }): void;
}

@Injectable({ providedIn: 'root'})
export class AppInsightsService {

  private config: Microsoft.ApplicationInsights.IConfig = {
    instrumentationKey: environment.applicationInsights.instrumentationKey,
    enableSessionStorageBuffer: false,
    maxBatchSizeInBytes: 64 * 1024,
    maxBatchInterval: 5000,
    isBeaconApiDisabled: false,
    disableExceptionTracking: true // handled in global-error-handler.ts
  };

  applicationHostDomain: string;
  user: IAuthenticationInfo;

  constructor(
    private store: Store<AppState>,
    private location: Location
  ) {
    // Clear AI buffer. 
    // It's not used anymore, but users may still have it in their session storage.
    try { sessionStorage.removeItem('AI_buffer'); } catch {}

    if (!AppInsights.config) {
      AppInsights.downloadAndSetup(this.config);

      (function installExceptionBurstAggregator() {
        const WINDOW_MS = 10000;
        const bursts = new Map<string, { suppressed: number; timer?: any; ex?: Error }>();
        const origTrackException = AppInsights.trackException;

        function keyOf(ex: Error) {
          return (ex?.name ?? 'Error') + '|' + (ex?.message ?? '') + '|' + String(ex?.stack ?? '').slice(0, 200);
        }

        function scheduleFlush(k: string) {
          const b = bursts.get(k)!;
          if (b.timer) return;
          b.timer = setTimeout(() => {
            const s = bursts.get(k)?.suppressed ?? 0;
            const ex = bursts.get(k)?.ex;
            bursts.delete(k);
            if (s > 0) {
              AppInsights.trackEvent('ExceptionBurstSummary', {
                exceptionName: ex?.name ?? 'Error',
                message: ex?.message ?? '',
                key: k,
                suppressed: String(s)
              });
            }
          }, WINDOW_MS);
        }

        AppInsights.trackException = function(ex: Error, handledAt?: string, props?: any, measurements?: any) {
          const k = keyOf(ex);
          const existing = bursts.get(k);
          if (!existing) {
            bursts.set(k, { suppressed: 0, ex });
            scheduleFlush(k);
            return origTrackException.call(AppInsights, ex, handledAt, props, measurements);
          } else {
            existing.suppressed++;
            scheduleFlush(k);
          }
        };
      })();
    }

    this.store.select(AppSelectors.selectLoggedInUser)
      .pipe(tap(user => this.user = user))
      .subscribe();
  }

  trackPageView(options?: { pageName?: string, props?: IPropDictionary, measurements?: IMeasurementDictionary }): void {
    options = options || {};
    options.pageName = options.pageName || this.getDefaultPageName();
    const url = this.getDefaultUrl();

    options.props = this.getProps(options.props);
    options.measurements = this.getMeasurements(options.measurements);

    AppInsights.trackPageView(options.pageName, url, options.props, options.measurements);
  }

  trackEvent(
    eventName: string,
    options?: {
      props?: IPropDictionary,
      measurements?: IMeasurementDictionary,
      routeInfo?: ActivatedRouteSnapshot
    }
  ): void {
    options = options || {};
    options.props = this.getProps(options.props, options.routeInfo);
    options.measurements = this.getMeasurements(options.measurements);

    AppInsights.trackEvent(eventName, options.props, options.measurements);
    AppInsights.flush();
  }

  trackException(
    ex: Error,
    options?: { handledAt?: string, props?: IPropDictionary, measurements?: IMeasurementDictionary }
  ): void {
    options = options || {};
    AppInsights.trackException(ex, options.handledAt, options.props, options.measurements);
  }

  trackTrace(message: string, options?: { props?: IPropDictionary }): void {
    options = options || {};
    options.props = this.getProps(options.props);
    AppInsights.trackTrace(message, options.props, AI.SeverityLevel.Warning);
  }

  setHostDomain(host: string) {
    this.applicationHostDomain = host;
  }

  private getProps(props: IPropDictionary, routeInfo?: ActivatedRouteSnapshot): IPropDictionary {
    props = props || {};

    const defaults: Record<string, string> = {
      App: environment.appName,
      UserName: '',
      ClientUserName: '',
      FirstName: '',
      LastName: '',
      UserRole: '',
      RoleEntity: ''
    };

    if (this.user) {
      defaults.UserName = this.user.userName;
      defaults.ClientUserName = this.user.clientUserName;
      defaults.FirstName = this.user.firstName;
      defaults.LastName = this.user.lastName;
      defaults.UserRole = this.user.role;
      defaults.RoleEntity = this.user.roleEntity;
    }

    if (routeInfo) {
      if (routeInfo.routeConfig?.path) {
        defaults['RoutePath'] = routeInfo.routeConfig.path;
      }
      if (routeInfo.data?.title) {
        defaults['Title'] = String(routeInfo.data.title);
      }
      try {
        (routeInfo.params as any)?.forEach?.((el: [string, string]) => {
          const [key, value] = el;
          defaults['PathParam_' + key] = String(value);
        });
      } catch {}
    }

    return { ...defaults, ...props };
  }

  private getMeasurements(measurements: IMeasurementDictionary): IMeasurementDictionary {
    return measurements || {};
  }

  private getDefaultPageName(): string {
    return '???';
  }

  private getDefaultUrl(): string {
    let path = this.location.path() || '';
    const idx = path.lastIndexOf('/');
    if (idx === path.length - 1) {
      path = path.substring(0, path.length - 1);
    }
    return path;
  }
}
